import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import App from './App';
import AppCrashBoundary from './components/AppCrashBoundary';
import { reportCrashToServer } from './services/crashReporter';
import { getErrorMessage, hardReloadForUpdatedAssets, isAssetLoadFailure } from './services/assetLoadRecovery';
import { AuthProvider } from './auth/AuthContext';
import muiTheme from './theme/muiTheme';
import { ToastProvider } from './ui/ToastProvider';
import NativeBackNavigation from './mobile/NativeBackNavigation';
import { isNativePlatform } from './mobile/capacitorPlatform';
import { initializeOfflineStorage, getEffectiveStorageEngine } from './services/offlineStorageAdapter';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import './styles/app.css';

async function disableServiceWorkerInNativeShell() {
    if (typeof window === 'undefined') {
        return;
    }

    if (!isNativePlatform()) {
        return;
    }

    try {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((registration) => registration.unregister()));
        }

        if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((key) => caches.delete(key)));
        }
    } catch {
        // Best effort: la app no debe fallar por esta limpieza.
    }
}

void disableServiceWorkerInNativeShell();

void initializeOfflineStorage().then(() => {
    const status = getEffectiveStorageEngine();
    if (status.warning) {
        console.warn(status.warning);
    } else {
        console.log(`[STORAGE] Motor offline inicializado: ${status.engine} (nativo: ${status.isNative})`);
    }
}).catch((err) => {
    console.error('[STORAGE] Error inicializando almacenamiento offline:', err);
});

function buildSupportCode() {
    const compact = new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14);
    return `CV-ERR-${compact || 'UNKNOWN'}`;
}

function buildGlobalCrashPayload(channel, errorLike) {
    const supportCode = buildSupportCode();
    const href = typeof window !== 'undefined' ? window.location.href : 'n/a';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a';
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const appVersion = String(import.meta.env.VITE_APP_VERSION || 'desconocida');
    const errorDigest = getErrorMessage(errorLike) || 'error-no-determinado';

    return {
        support_code: supportCode,
        error_digest: errorDigest,
        report_text: [
            `Codigo: ${supportCode}`,
            `Fecha: ${new Date().toISOString()}`,
            `Canal: ${channel}`,
            `VersionApp: ${appVersion}`,
            `Online: ${online ? 'si' : 'no'}`,
            `URL: ${href}`,
            `UserAgent: ${userAgent}`,
            `Error: ${errorDigest}`,
        ].join('\n'),
        url: href,
        app_version: appVersion,
        channel,
        context: {
            online,
            user_agent: userAgent,
        },
    };
}

window.addEventListener('error', (event) => {
    const errorLike = event?.error || event?.message;

    if (isAssetLoadFailure(errorLike)) {
        void hardReloadForUpdatedAssets();
        return;
    }

    void reportCrashToServer(buildGlobalCrashPayload('window-error', errorLike));
});

window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;

    if (isAssetLoadFailure(reason)) {
        void hardReloadForUpdatedAssets();
        return;
    }

    void reportCrashToServer(buildGlobalCrashPayload('window-unhandledrejection', reason));
});

createRoot(document.getElementById('react-root')).render(
    <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <AppCrashBoundary>
            <ToastProvider>
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <AuthProvider>
                        <NativeBackNavigation />
                        <App />
                    </AuthProvider>
                </BrowserRouter>
            </ToastProvider>
        </AppCrashBoundary>
    </ThemeProvider>,
);
